// useAudienceSearchWithFilters.js - FIXED: Moved triggerSearch function and added LLM support

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAudienceFilterDefaults } from "../context/envDefaults";
import audienceApiService from "../services/audienceApiService";
import { getRecommendedThreshold, relevanceFromDistance } from "../utils";

// Fallback cosine distances for audience terms, from the threshold sheet.
// Include uses the default (Strong Match) tier; exclude keeps the tighter
// Best Match distance, matching the pre-migration relationship between them.
const AUDIENCE_INCLUDE_THRESHOLD = getRecommendedThreshold("AUDIENCE");
const AUDIENCE_EXCLUDE_THRESHOLD = getRecommendedThreshold(
	"AUDIENCE",
	"bestMatch",
);
const AUDIENCE_INCLUDE_RELEVANCE = Math.round(
	relevanceFromDistance(AUDIENCE_INCLUDE_THRESHOLD, "AUDIENCE"),
);
const AUDIENCE_EXCLUDE_RELEVANCE = Math.round(
	relevanceFromDistance(AUDIENCE_EXCLUDE_THRESHOLD, "AUDIENCE"),
);

// Custom debounce utility
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => clearTimeout(handler);
	}, [value, delay]);

	return debouncedValue;
};

// CSV parsing utility for audience IDs
const parseAudienceCSV = (csvText) => {
	const lines = csvText
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line);
	const audienceIds = [];

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];

		if (
			i === 0 &&
			(line.toLowerCase().includes("audience") ||
				line.toLowerCase().includes("id") ||
				line.toLowerCase().includes("openaudience"))
		) {
			continue;
		}

		const values = line
			.split(",")
			.map((val) => val.trim().replace(/^["']|["']$/g, ""));

		for (const value of values) {
			if (value && isValidAudienceId(value)) {
				audienceIds.push(value);
				break;
			}
		}
	}

	return audienceIds;
};

const isValidAudienceId = (id) => {
	if (!id || typeof id !== "string") return false;
	return /^[a-zA-Z0-9_-]+$/.test(id.trim());
};

export const useAudienceSearchWithFilters = (
	initialFilters = {},
	options = {},
) => {
	const {
		onFiltersChange,
		onUploadAudienceList,
		onUploadBlockedAudienceList,
		enableScrollPositionSaving = true,
	} = options;

	// Scroll position preservation
	const filtersContentRef = useRef(null);
	const savedScrollPositions = useRef({});

	// Sort options with comprehensive list
	const sortOptions = [
		{ value: "compositeScore", label: "Composite Score" },
		{ value: "soldImpressions", label: "Impressions" },
		{ value: "estimated_reach", label: "Estimated Reach" },
		{ value: "clickThroughRate", label: "CTR" },
		{ value: "reach", label: "Reach" },
		{ value: "addressable_reach", label: "Addressable Reach" },
		{ value: "CPRMarketRequests", label: "CPR Market Requests" },
		{ value: "uniqueUsers", label: "Unique Users" },
		{ value: "revenuePerImpression", label: "Revenue per Impression" },
		{ value: "spendPerImpression", label: "Spend per Impression" },
		{ value: "vtr", label: "VTR" },
		{ value: "viewability", label: "Viewability" },
		{ value: "oxWinRate", label: "OX Win Rate" },
		{ value: "pubWinRate", label: "Pub Win Rate" },
		{ value: "bidCpm", label: "Bid CPM" },
		{ value: "bidGuidance", label: "Bid Guidance" },
	];

	// Sort direction options
	const sortDirectionOptions = [
		{ value: "DESC", label: "Descending (High to Low)" },
		{ value: "ASC", label: "Ascending (Low to High)" },
	];

	// Default filters structure
	const defaultFilters = useMemo(() => getAudienceFilterDefaults(), []);

	// Combined state
	const [filters, setFilters] = useState({
		...defaultFilters,
		...initialFilters,
	});

	// Filter values state from API
	const [filterValues, setFilterValues] = useState({});
	const [filterValuesLoading, setFilterValuesLoading] = useState(false);
	const [filterValuesError, setFilterValuesError] = useState(null);
	const [filterValuesLastFetch, setFilterValuesLastFetch] = useState(null);

	// Expanded sections state
	const [expandedSections, setExpandedSections] = useState({
		search: false,
		audienceProperties: false,
		metrics: false,
		advancedMetrics: false,
		advanced: false,
	});

	// Category expansion state
	const [expandedCategories, setExpandedCategories] = useState({
		sourceProvider: false,
		audienceAttributes: false,
		audienceDescriptors: false,
		scalePerformance: false,
	});

	// Search input states with debouncing
	const [searchInputs, setSearchInputs] = useState({
		accountNameSearch: "",
		audienceIdSearch: "",
		directAudienceProviderSearch: "",
		publisherAccountIdSearch: "",
	});

	// Debounced search values
	const debouncedSearchInputs = {
		accountNameSearch: useDebounce(searchInputs.accountNameSearch, 300),
		audienceIdSearch: useDebounce(searchInputs.audienceIdSearch, 300),
		directAudienceProviderSearch: useDebounce(
			searchInputs.directAudienceProviderSearch,
			300,
		),
		publisherAccountIdSearch: useDebounce(
			searchInputs.publisherAccountIdSearch,
			300,
		),
	};

	// Upload state management
	const [uploadStates, setUploadStates] = useState({
		includeList: {
			isUploading: false,
			fileName: null,
			audienceIds: [],
			error: null,
			isUploaded: false,
		},
		blockedList: {
			isUploading: false,
			fileName: null,
			audienceIds: [],
			error: null,
			isUploaded: false,
		},
	});

	// UI state
	const [showUploadTooltip, setShowUploadTooltip] = useState(false);

	// Search state
	const [searchQuery, setSearchQuery] = useState("");
	const [refinedQuery, setRefinedQuery] = useState("");
	const [searchTerms, setSearchTerms] = useState([]);
	const [excludeTerms, setExcludeTerms] = useState([]);
	const [isSearching, setIsSearching] = useState(false);

	const [searchResults, setSearchResults] = useState({
		success: false,
		audiences: [],
		totalRecords: 0,
		searchSummary: null,
		hasMore: false,
		currentPage: 1,
		totalPages: 1,
	});

	const [error, setError] = useState(null);
	const [hasSearched, setHasSearched] = useState(false);

	// Search options with comprehensive sort handling
	const [searchOptions, setSearchOptions] = useState({
		sortBy: "compositeScore",
		sortOrder: "DESC",
		currentPage: 0,
		pageSize: 100,
	});

	// Build complete search payload function
	const buildCompleteSearchPayload = useCallback(
		(searchQueryData = {}, currentFilters = null) => {
			console.log(
				"Building payload with EXACT original structure but current filter values",
			);
			const filtersToUse = currentFilters || filters;

			const {
				query = [],
				excludeQuery = [],
				searchOptions = {},
			} = searchQueryData;

			// KEEP EXACT ORIGINAL STRUCTURE FROM FUNCTIONAL PAYLOAD
			const completePayload = {
				query: query,
				excludeQuery: excludeQuery,
				filters: {
					audienceVector: {
						accountId: null,
						accountName:
							filtersToUse.accountName?.length > 0
								? filtersToUse.accountName
								: null,
						description: null,
						audienceName: filtersToUse.openAudienceId?.trim() || null,
						type: filtersToUse.type?.length > 0 ? filtersToUse.type : null,
						dataSource:
							filtersToUse.dataSource?.length > 0
								? filtersToUse.dataSource
								: null,
						region:
							filtersToUse.region?.length > 0 ? filtersToUse.region : null,
						estimatedReach: filtersToUse.estimated_reach || 0,
						definition: null,
						jobId: null,
						reach: filtersToUse.reach || 0,
						bqTable: null,
						isArchived: false,
						bqReachTable: null,
						normalizedDefinition: null,
						reportStatus: null,
						info: null,
						error: null,
						tags: null,
						directAudienceProvider:
							filtersToUse.directAudienceProvider?.length > 0
								? filtersToUse.directAudienceProvider
								: null,
						exportType: null,
						userId: null,
						exportTargets: null,
						lastUdsTimestamp: null,
						errorCode: null,
						lastExportId: null,
						createdWith:
							filtersToUse.createdWith?.length > 0
								? filtersToUse.createdWith
								: null,
						shareableOpenxAudience:
							(filtersToUse.shareable_openx_audience &&
								filtersToUse.shareable_openx_audience.includes(true)) ||
							false,
						pricing:
							filtersToUse.pricing?.length > 0 ? filtersToUse.pricing : null,
						createdAt: null,
						cleanAudienceName: null,
						primaryPersona:
							filtersToUse.primaryPersona?.length > 0
								? filtersToUse.primaryPersona
								: null,
						audienceStrategy:
							filtersToUse.audienceStrategy?.length > 0
								? filtersToUse.audienceStrategy
								: null,
						technicalAttributes: null,
						status:
							filtersToUse.status?.length > 0 ? filtersToUse.status : null,
						notesDescription: null,
						hasMeta: false,
						entity:
							filtersToUse.entity?.length > 0 ? filtersToUse.entity : null,
						event: filtersToUse.event?.length > 0 ? filtersToUse.event : null,
					},
					audienceMetrics: {
						addressableReach: filtersToUse.addressable_reach || 0,
						reportMonth: null,
						audienceId: null,
						status: null,
						reach: filtersToUse.reach || 0,
						requests: filtersToUse.CPRMarketRequests || 0,
						soldImpressions: filtersToUse.Impressions || 0,
						uniqueUsers: filtersToUse.uniqueUsers || 0,
						uniqueUsersWithRequests: filtersToUse.uniqueUsersWithRequests || 0,
						uniqueUsersWithImpressions:
							filtersToUse.uniqueUsersWithImpressions || 0,
						cookies: filtersToUse.cookies || 0,
						cookiesWithRequests: filtersToUse.cookiesWithRequests || 0,
						cookiesWithImpressions: filtersToUse.cookiesWithImpressions || 0,
						desktopsWithRequests: filtersToUse.desktopsWithRequests || 0,
						mobileDevices: filtersToUse.mobileDevices || 0,
						mobileDevicesWithRequests:
							filtersToUse.mobileDevicesWithRequests || 0,
						mobileDevicesWithImpressions:
							filtersToUse.mobileDevicesWithImpressions || 0,
						tabletDevices: filtersToUse.tabletDevices || 0,
						tabletDevicesWithRequests:
							filtersToUse.tabletDevicesWithRequests || 0,
						tabletDevicesWithImpressions:
							filtersToUse.tabletDevicesWithImpressions || 0,
						connectedTvDevices: filtersToUse.connectedTvDevices || 0,
						connectedTvDevicesWithRequests:
							filtersToUse.connectedTvDevicesWithRequests || 0,
						connectedTvDevicesWithImpressions:
							filtersToUse.connectedTvDevicesWithImpressions || 0,
						displayRequests: filtersToUse.displayRequests || 0,
						videoRequests: filtersToUse.videoRequests || 0,
						mobileappRequests: filtersToUse.mobileappRequests || 0,
						mobilewebRequests: filtersToUse.mobilewebRequests || 0,
						desktopRequests: filtersToUse.desktopRequests || 0,
						tabletRequests: filtersToUse.tabletRequests || 0,
						households: filtersToUse.households || 0,
						householdsWithRequests: filtersToUse.householdsWithRequests || 0,
						householdsWithImpressions:
							filtersToUse.householdsWithImpressions || 0,
						idlXyWithRequests: filtersToUse.idlXyWithRequests || 0,
						idlXiWithRequests: filtersToUse.idlXiWithRequests || 0,
						idlXcWithRequests: filtersToUse.idlXcWithRequests || 0,
						idlXmWithRequests: filtersToUse.idlXmWithRequests || 0,
						idlXyWithImpressions: filtersToUse.idlXyWithImpressions || 0,
						idlXiWithImpressions: filtersToUse.idlXiWithImpressions || 0,
						idlXcWithImpressions: filtersToUse.idlXcWithImpressions || 0,
						idlXmWithImpressions: filtersToUse.idlXmWithImpressions || 0,
						id5s: filtersToUse.id5s || 0,
						id5WithRequests: filtersToUse.id5WithRequests || 0,
						id5WithImpressions: filtersToUse.id5WithImpressions || 0,
						connectedTvDeviceIdRequests:
							filtersToUse.connectedTvDeviceIdRequests || 0,
						connectedTvDeviceIdSoldImpressions:
							filtersToUse.connectedTvDeviceIdSoldImpressions || 0,
						connectedTvDeviceIdDevices:
							filtersToUse.connectedTvDeviceIdDevices || 0,
						connectedTvDeviceIdSoldImpressionsWithRequests:
							filtersToUse.connectedTvDeviceIdSoldImpressionsWithRequests || 0,
						connectedTvDeviceIdSoldImpressionsWithImpressions:
							filtersToUse.connectedTvDeviceIdSoldImpressionsWithImpressions ||
							0,
						uniqueUsersViaConnectedTv:
							filtersToUse.uniqueUsersViaConnectedTv || 0,
						uniqueUsersViaConnectedTvWithRequests:
							filtersToUse.uniqueUsersViaConnectedTvWithRequests || 0,
						uniqueUsersViaConnectedTvWithImpressions:
							filtersToUse.uniqueUsersViaConnectedTvWithImpressions || 0,
						idlXyViaConnectedTv: filtersToUse.idlXyViaConnectedTv || 0,
						idlXyViaConnectedTvWithRequests:
							filtersToUse.idlXyViaConnectedTvWithRequests || 0,
						idlXyViaConnectedTvWithImpressions:
							filtersToUse.idlXyViaConnectedTvWithImpressions || 0,
						idlXiViaConnectedTv: filtersToUse.idlXiViaConnectedTv || 0,
						idlXiViaConnectedTvWithRequests:
							filtersToUse.idlXiViaConnectedTvWithRequests || 0,
						idlXiViaConnectedTvWithImpressions:
							filtersToUse.idlXiViaConnectedTvWithImpressions || 0,
						uniqueIpsWithRequests: filtersToUse.uniqueIpsWithRequests || 0,
						ipRequests: filtersToUse.ipRequests || 0,
						advertiserSpend: filtersToUse.CPRMarketDemandPartnerSpendInUSD || 0,
						avgSpendPerUniqueUser: filtersToUse.avgSpendPerUniqueUser || 0,
						avgEcpm: filtersToUse.avgEcpm || 0,
						stddevEcpm: filtersToUse.stddevEcpm || 0,
						minEcpm: filtersToUse.minEcpm || 0,
						maxEcpm: filtersToUse.maxEcpm || 0,
						impressionFrequency: filtersToUse.impressionFrequency || 0,
						impressionFrequencyPerDay:
							filtersToUse.impressionFrequencyPerDay || 0,
						connectedTvDevices: filtersToUse.connectedTvDevices || 0,
						avgSpendPerUniqueConnectedTvDeviceId:
							filtersToUse.avgSpendPerUniqueConnectedTvDeviceId || 0,
						connectedTvDeviceIdAvgEcpm:
							filtersToUse.connectedTvDeviceIdAvgEcpm || 0,
						connectedTvDeviceIdStddevEcpm:
							filtersToUse.connectedTvDeviceIdStddevEcpm || 0,
						connectedTvDeviceIdMinEcpm:
							filtersToUse.connectedTvDeviceIdMinEcpm || 0,
						connectedTvDeviceIdMaxEcpm:
							filtersToUse.connectedTvDeviceIdMaxEcpm || 0,
						connectedTvDeviceIdImpressionFrequency:
							filtersToUse.connectedTvDeviceIdImpressionFrequency || 0,
						connectedTvDeviceIdImpressionFrequencyPerDay:
							filtersToUse.connectedTvDeviceIdImpressionFrequencyPerDay || 0,
						clickThroughRate: filtersToUse.ClickThroughRate || 0,
						revenuePerImpression:
							filtersToUse.RevenuePerImpression * 1000000 || 0,
						spendPerImpression: filtersToUse.SpendPerImpression * 1000000 || 0,
						vtr: filtersToUse.VTR || 0,
						viewability: filtersToUse.Viewability || 0,
						oxWinRate: filtersToUse.ox_win_rate || 0,
						pubWinRate: filtersToUse.pub_win_rate || 0,
						bidCpm: filtersToUse.bid_cpm * 1000000 || 0,
						bidGuidance: filtersToUse.Bid_Guidance * 1000000 || 0,
						dealIds: null,
						dealNks: null,
						cprStartFirstOccurrence: filtersToUse.cprStartFirstOccurrence || 0,
						cprMarketDemandPartnerSpend:
							filtersToUse.cprMarketDemandPartnerSpend * 1000000 || 0,
						cprMarketPublisherRevenueInUSD:
							filtersToUse.CPRMarketPublisherRevenueInUSD * 1000000 || 0,
						completeFirstOccurrence: filtersToUse.completeFirstOccurrence || 0,
						fills: filtersToUse.fills || 0,
						nonZeroBids: filtersToUse.NonZeroBids || 0,
						impressions: filtersToUse.Impressions || 0,
						dealVolumeDealInfo: filtersToUse.dealVolumeDealInfo || 0,
						nonZeroBidsValueInUSD:
							filtersToUse.nonZeroBidsValueInUSD * 1000000 || 0,
						bidSpendInUSD: filtersToUse.BidSpendInUSD * 1000000 || 0,
						startFirstOccurrence: filtersToUse.startFirstOccurrence || 0,
						viewables: null,
						viewabilityMeasures: null,
					},
					cumulativeDataFilters: {
						cumulativeSetsFiltersMap: {
							Relevance: {
								sortBy: filtersToUse.sortBy || "compositeScore",
								isASC: (filtersToUse.sortDirection || "DESC") === "ASC",
								filterBy: "reach",
								threshold: 1000,
							},
							Performance: {
								sortBy: "avgCpm",
								isASC: false,
								filterBy: "avgCpm",
								threshold: 5000.5,
							},
							CostOptimized: {
								sortBy: "reach",
								isASC: true,
								filterBy: "reach",
								threshold: 1000,
							},
							Relavance: {
								sortBy: "reach",
								isASC: true,
								filterBy: "reach",
								threshold: 1000,
							},
							Perfomance: {
								sortBy: "avgCpm",
								isASC: false,
								filterBy: "avgCpm",
								threshold: 5000.5,
							},
							costoptimized: {
								sortBy: "reach",
								isASC: true,
								filterBy: "reach",
								threshold: 1000,
							},
						},
					},
				},
				sortBy: searchOptions.sortBy || "compositeScore",
				view: searchOptions.sortOrder || "DESC",
				startIndex: searchOptions.startIndex || filtersToUse.startIndex || 0,
				noOfResult: filtersToUse.noOfResult || 200,
				noOfDays: 30,
				isSegmentsRequire: false,
			};

			console.log(
				"Payload built with EXACT structure + current values:",
				completePayload,
			);
			return completePayload;
		},
		[filters],
	);

	// MAIN SEARCH FUNCTION - Enhanced search function with multiple entry points
	const handleAudienceSearch = useCallback(
		async (searchData, searchOptions = {}, completeFilters = null) => {
			try {
				setIsSearching(true);
				setError(null);
				setHasSearched(true);

				console.log("Starting search with EXACT structure + current filters");

				// Support multiple search data formats
				let searchTerms = [];

				// Handle object with query/excludeQuery arrays (new format)
				if (
					searchData &&
					typeof searchData === "object" &&
					!Array.isArray(searchData)
				) {
					if (searchData.query && Array.isArray(searchData.query)) {
						searchData.query.forEach((item) => {
							searchTerms.push({
								text: item.query,
								type: "include",
								score: item.score || AUDIENCE_INCLUDE_RELEVANCE,
								threshold: item.threshold ?? AUDIENCE_INCLUDE_THRESHOLD,
								originalScore: item.originalScore,
								frontendScore: item.frontendScore,
							});
						});
					}
					if (
						searchData.excludeQuery &&
						Array.isArray(searchData.excludeQuery)
					) {
						searchData.excludeQuery.forEach((item) => {
							searchTerms.push({
								text: item.query,
								type: "exclude",
								score: item.score || AUDIENCE_EXCLUDE_RELEVANCE,
								threshold: item.threshold ?? AUDIENCE_EXCLUDE_THRESHOLD,
								originalScore: item.originalScore,
								frontendScore: item.frontendScore,
							});
						});
					}
				}
				// Handle array of search terms (existing format)
				else if (Array.isArray(searchData)) {
					searchTerms = searchData;
				}
				// Handle string search (simple format)
				else if (typeof searchData === "string") {
					searchTerms = [
						{
							text: searchData,
							type: "include",
							score: AUDIENCE_INCLUDE_RELEVANCE,
							threshold: AUDIENCE_INCLUDE_THRESHOLD,
						},
					];
				}

				// Check authentication
				if (!audienceApiService.isAuthenticated()) {
					return { success: false, error: "Authentication required" };
				}

				// Enhanced options extraction with backward compatibility
				const {
					sortBy: passedSortBy,
					sortOrder: passedSortOrder,
					startIndex = 0,
					performanceMetric,
					minValue,
					pageSize,
					selectedTier,
					// Legacy support
					sortDirection: legacySortDirection,
				} = searchOptions;

				// Determine final sort values with priority order
				const finalSortBy = passedSortBy || "compositeScore";
				const finalSortOrder =
					passedSortBy === "score"
						? passedSortOrder === "ASC"
							? "DESC"
							: "ASC"
						: passedSortOrder || "DESC";
				const finalPageSize = filters.noOfResult || 200;

				// Use provided complete filters or build from current state
				const filtersToUse = completeFilters || filters;

				// Build payload with enhanced flexibility
				const completePayload = buildCompleteSearchPayload(
					{
						query: searchTerms
							.filter((term) => term.type === "include")
							.map((term) => ({
								query: term.text,
								threshold: term.threshold ?? AUDIENCE_INCLUDE_THRESHOLD,
								originalScore: term.originalScore,
								frontendScore: term.frontendScore,
							})),
						excludeQuery: searchTerms
							.filter((term) => term.type === "exclude")
							.map((term) => ({
								query: term.text,
								threshold: term.threshold ?? AUDIENCE_EXCLUDE_THRESHOLD,
								originalScore: term.originalScore,
								frontendScore: term.frontendScore,
							})),
						searchOptions: {
							sortBy: finalSortBy,
							sortOrder: finalSortOrder,
							startIndex,
						},
					},
					filtersToUse,
				);

				console.log(
					"Sending payload with EXACT structure + current filter values",
				);

				// Call API with complete payload
				const result =
					await audienceApiService.searchAudiences(completePayload);

				if (result.success) {
					const audienceData = Array.isArray(result.data) ? result.data : [];
					const searchResultsData = {
						success: true,
						audiences: audienceData,
						totalRecords: result.totalRecords || audienceData.length,
						searchSummary: {
							totalResults: result.totalRecords || audienceData.length,
							audiences: audienceData.length,
							searchTime: new Date().toISOString(),
							query:
								completePayload.query?.map((q) => q.query).join(", ") ||
								"Filter-based search",
							hasSearchTerms: completePayload.query.length > 0,
							hasActiveFilters: true,
							appliedFilters: {
								accountName: completePayload.filters.audienceVector.accountName,
								status: completePayload.filters.audienceVector.status,
								reach: completePayload.filters.audienceVector.reach,
								sortBy: completePayload.sortBy,
							},
						},
					};

					setSearchResults(searchResultsData);
					console.log(
						"Search successful with correct structure:",
						audienceData.length,
						"results",
					);
					return {
						success: true,
						data: searchResultsData,
						audiences: audienceData,
					};
				} else {
					throw new Error(result.error || "Search failed");
				}
			} catch (err) {
				console.error("Search error:", err);
				const errorMessage = err.message || "Audience search failed";
				setError(errorMessage);
				setSearchResults({
					success: false,
					audiences: [],
					totalRecords: 0,
					searchSummary: null,
				});
				return { success: false, error: errorMessage };
			} finally {
				setIsSearching(false);
			}
		},
		[filters, buildCompleteSearchPayload],
	);

	// MOVED UP: triggerSearch function - now defined early so it can be used
	const triggerSearch = useCallback(
		(searchOptions = {}) => {
			console.log("triggerSearch called with options:", searchOptions);
			console.log("Current searchTerms:", searchTerms);
			console.log("Current excludeTerms:", excludeTerms);

			// Build the search data directly from current state
			const searchData = {
				query: searchTerms.map((term) => ({
					query: term.query || term.text,
					threshold: term.threshold ?? AUDIENCE_INCLUDE_THRESHOLD,
				})),
				excludeQuery: excludeTerms.map((term) => ({
					query: term.query || term.text,
					threshold: term.threshold ?? AUDIENCE_EXCLUDE_THRESHOLD,
				})),
			};

			console.log("Sending search data:", searchData);
			console.log("Query array:", searchData.query);
			console.log("Using sort options from interface:", searchOptions);

			// Pass the search options to handleAudienceSearch
			return handleAudienceSearch(searchData, searchOptions);
		},
		[searchTerms, excludeTerms, handleAudienceSearch],
	);

	// LLM SUPPORT FUNCTIONS
	const injectSearchResults = useCallback(
		(externalResults) => {
			console.log("Injecting external search results:", externalResults);

			if (
				externalResults &&
				externalResults.success &&
				externalResults.audiences
			) {
				// Get current exclude terms for client-side filtering
				const currentExcludeTerms = excludeTerms.map((term) =>
					(term.text || term.query || term).toLowerCase(),
				);

				console.log(
					"Current exclude terms for filtering:",
					currentExcludeTerms,
				);

				// Filter out audiences that match exclude terms (client-side safety check)
				let filteredAudiences = externalResults.audiences;

				if (currentExcludeTerms.length > 0) {
					filteredAudiences = externalResults.audiences.filter((audience) => {
						// Check audience name and other searchable fields
						const searchableText = [
							audience.audienceVector?.audienceName,
							audience.audienceVector?.cleanAudienceName,
							audience.audienceVector?.description,
							audience.audienceVector?.primaryPersona,
							audience.audienceVector?.audienceStrategy,
						]
							.filter(Boolean)
							.join(" ")
							.toLowerCase();

						// Return false (exclude) if any exclude term is found
						const shouldExclude = currentExcludeTerms.some((excludeTerm) =>
							searchableText.includes(excludeTerm),
						);

						if (shouldExclude) {
							console.log(
								`Excluding audience: ${audience.audienceVector?.audienceName} - matches exclude term`,
							);
						}

						return !shouldExclude;
					});

					console.log(
						`Filtered ${externalResults.audiences.length - filteredAudiences.length} audiences based on exclude terms`,
					);
				}

				// Set the filtered search results
				setSearchResults({
					success: true,
					audiences: filteredAudiences,
					totalRecords: filteredAudiences.length, // Update count to filtered count
					metadata: externalResults.metadata || {},
					searchTime: externalResults.searchTime || 0,
					searchSummary: {
						totalResults: filteredAudiences.length,
						audiences: filteredAudiences.length,
						searchTime: new Date().toISOString(),
						query:
							externalResults.originalData?.metadata?.originalQuery ||
							"LLM Search",
						hasSearchTerms: true,
						hasActiveFilters: false,
						appliedFilters: {},
						excludeTermsApplied:
							currentExcludeTerms.length > 0 ? currentExcludeTerms : undefined,
					},
				});

				setHasSearched(true);
				setIsSearching(false);
				setError(null);

				console.log("External results injected and filtered successfully");
				return true;
			}

			console.warn("Invalid external results provided");
			return false;
		},
		[excludeTerms],
	);

	// Override search capability for LLM results
	const triggerSearchWithOverride = useCallback(
		(searchTermsArray, overrideResults = null) => {
			console.log("Trigger search with override:", {
				searchTermsArray,
				hasOverride: !!overrideResults,
			});

			if (overrideResults) {
				// Use provided results instead of API call
				const success = injectSearchResults(overrideResults);
				if (success) {
					// Update search terms for UI
					// Update search terms for UI - properly separate include and exclude
					if (searchTermsArray && searchTermsArray.length > 0) {
						const includeTermsFromArray = [];
						const excludeTermsFromArray = [];

						searchTermsArray.forEach((term) => {
							const formattedTerm = {
								query: term.text || term.query || term,
								text: term.text || term.query || term,
								type: term.type || "include",
								score:
									term.score ||
									(term.type === "exclude"
										? AUDIENCE_EXCLUDE_RELEVANCE
										: AUDIENCE_INCLUDE_RELEVANCE),
								threshold:
									term.threshold ??
									(term.type === "exclude"
										? AUDIENCE_EXCLUDE_THRESHOLD
										: AUDIENCE_INCLUDE_THRESHOLD),
							};

							if (term.type === "exclude") {
								excludeTermsFromArray.push(formattedTerm);
							} else {
								includeTermsFromArray.push(formattedTerm);
							}
						});

						console.log("Setting separated terms:", {
							includeTerms: includeTermsFromArray,
							excludeTerms: excludeTermsFromArray,
						});

						setSearchTerms(includeTermsFromArray);
						setExcludeTerms(excludeTermsFromArray);
					}
					return Promise.resolve({ success: true, data: overrideResults });
				}
			}

			// Fall back to normal search if override fails
			return triggerSearch();
		},
		[injectSearchResults, triggerSearch],
	);

	// Load filter values function
	const loadFilterValues = useCallback(
		async (forceRefresh = false) => {
			if (filterValuesLoading && !forceRefresh) return;

			setFilterValuesLoading(true);
			setFilterValuesError(null);

			try {
				console.log("Loading filter values from API...");
				const response = await audienceApiService.getFilterValues(forceRefresh);

				if (response.success) {
					const apiFilterValues = response.data?.audienceFilterValues || {};
					console.log(
						"Filter values loaded successfully:",
						Object.keys(apiFilterValues),
					);

					setFilterValues(apiFilterValues);
					setFilterValuesLastFetch(new Date());
					setFilterValuesError(null);
				} else {
					console.error("Failed to load filter values:", response.error);
					setFilterValuesError(
						response.error || "Failed to load filter values",
					);
				}
			} catch (error) {
				console.error("Error loading filter values:", error);
				setFilterValuesError(error.message || "Failed to load filter values");
			} finally {
				setFilterValuesLoading(false);
			}
		},
		[filterValuesLoading],
	);

	// Load filter values on mount
	useEffect(() => {
		loadFilterValues();
	}, []);

	// Available options with comprehensive sort options
	const availableOptions = useMemo(() => {
		const fallbacks = {
			statuses: ["PROCESSED", "BUILDING", "READY", "ERROR", "ARCHIVED"],
			pricingOptions: [],
			typeOptions: [],
			dataSourceOptions: [],
			regionOptions: [],
			accountNameOptions: [],
			entityOptions: [],
			eventOptions: [],
			primaryPersonaOptions: [],
			audienceStrategyOptions: [],
			createdWithOptions: [],
			directAudienceProviderOptions: [],
		};

		if (Object.keys(filterValues).length === 0) {
			console.log("Using fallback filter options (API values not loaded)");
			return {
				...fallbacks,
				sortByOptions: sortOptions,
				sortDirectionOptions: sortDirectionOptions,
			};
		}

		return {
			statuses: filterValues.status || fallbacks.statuses,
			pricingOptions: filterValues.pricing || fallbacks.pricingOptions,
			typeOptions: filterValues.type || fallbacks.typeOptions,
			dataSourceOptions: filterValues.dataSource || fallbacks.dataSourceOptions,
			regionOptions: filterValues.region || fallbacks.regionOptions,
			accountNameOptions:
				filterValues.accountName || fallbacks.accountNameOptions,
			directAudienceProviderOptions:
				filterValues.directAudienceProvider ||
				fallbacks.directAudienceProviderOptions,
			entityOptions: filterValues.entity || fallbacks.entityOptions,
			eventOptions: filterValues.event || fallbacks.eventOptions,
			primaryPersonaOptions:
				filterValues.primaryPersona || fallbacks.primaryPersonaOptions,
			audienceStrategyOptions:
				filterValues.audienceStrategy || fallbacks.audienceStrategyOptions,
			createdWithOptions:
				filterValues.createdWith || fallbacks.createdWithOptions,
			sortByOptions: sortOptions,
			sortDirectionOptions: sortDirectionOptions,
		};
	}, [filterValues]);

	// Scroll position management functions
	const saveScrollPosition = useCallback(() => {
		if (!enableScrollPositionSaving || !filtersContentRef.current) return;
		savedScrollPositions.current.main = filtersContentRef.current.scrollTop;
	}, [enableScrollPositionSaving]);

	const restoreScrollPosition = useCallback(() => {
		if (!enableScrollPositionSaving) return;
		setTimeout(() => {
			if (
				filtersContentRef.current &&
				savedScrollPositions.current.main !== undefined
			) {
				filtersContentRef.current.scrollTop = savedScrollPositions.current.main;
			}
		}, 0);
	}, [enableScrollPositionSaving]);

	// Filter update handlers
	const updateFilter = useCallback(
		(filterKey, value) => {
			console.log(`updateFilter: ${filterKey} =`, value);
			saveScrollPosition();

			let processedValue = value;

			if (filterKey === "noOfResult") {
				processedValue = parseInt(value, 10);
				if (isNaN(processedValue) || processedValue < 0) {
					processedValue = 200;
				}
			}

			// Handle numeric filters with validation
			const numericFilters = [
				"reach",
				"estimated_reach",
				"addressable_reach",
				"CPRMarketRequests",
				"Impressions",
				"ImpressionsWithClickMacro",
				"Clicks",
				"NonZeroBids",
				"VTR",
				"Viewability",
				"ClickThroughRate",
				"CPRMarketDemandPartnerSpendInUSD",
				"CPRMarketPublisherRevenueInUSD",
				"BidSpendInUSD",
				"RevenuePerImpression",
				"SpendPerImpression",
				"bid_cpm",
				"Bid_Guidance",
				"ox_win_rate",
				"pub_win_rate",
			];

			if (numericFilters.includes(filterKey)) {
				processedValue = Math.max(0, Number(value) || 0);
			}

			setFilters((prev) => {
				const updated = { ...prev, [filterKey]: processedValue };
				console.log(
					`Filter updated in state: ${filterKey} = ${processedValue}`,
				);

				if (onFiltersChange && prev[filterKey] !== processedValue) {
					setTimeout(() => onFiltersChange(updated), 0);
				}

				return updated;
			});
		},
		[onFiltersChange, saveScrollPosition],
	);

	const toggleArrayFilter = useCallback(
		(filterKey, value) => {
			console.log(`toggleArrayFilter: ${filterKey} toggle`, value);
			saveScrollPosition();

			setFilters((prev) => {
				const currentArray = prev[filterKey] || [];
				const updated = {
					...prev,
					[filterKey]: currentArray.includes(value)
						? currentArray.filter((item) => item !== value)
						: [...currentArray, value],
				};

				console.log(
					`Array filter updated in state: ${filterKey} =`,
					updated[filterKey],
				);

				if (onFiltersChange) {
					setTimeout(() => onFiltersChange(updated), 0);
				}

				return updated;
			});
		},
		[onFiltersChange, saveScrollPosition],
	);

	// Clear all filters
	const clearAllFilters = useCallback(() => {
		console.log("🔧 Clearing all audience filters to env defaults");
		saveScrollPosition();

		const envDefaults = getAudienceFilterDefaults();
		setFilters({
			...envDefaults,
			...initialFilters,
		});

		if (onFiltersChange) {
			onFiltersChange({ ...envDefaults, ...initialFilters });
		}
	}, [saveScrollPosition, initialFilters, onFiltersChange]);

	// Section and category toggles
	const toggleSection = useCallback((sectionName) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionName]: !prev[sectionName],
		}));
	}, []);

	const toggleCategory = useCallback((categoryName) => {
		setExpandedCategories((prev) => ({
			...prev,
			[categoryName]: !prev[categoryName],
		}));
	}, []);

	// Search input handlers
	const handleSearchInputChange = useCallback((e) => {
		const value = e.target ? e.target.value : "";
		console.log("handleSearchInputChange called with event, value:", value);
		setSearchQuery(value);
	}, []);

	const onSearchInputChange = useCallback((value) => {
		console.log(
			"onSearchInputChange called with:",
			value,
			"type:",
			typeof value,
		);

		// Extract string value if it's an event object, otherwise use as-is
		let stringValue;
		if (value && typeof value === "object" && value.target) {
			// It's an event object
			stringValue = value.target.value;
			console.log("Extracted value from event:", stringValue);
		} else if (typeof value === "string") {
			// It's already a string
			stringValue = value;
		} else {
			// Convert to string as fallback
			stringValue = String(value || "");
		}

		console.log("Setting searchQuery to:", stringValue);
		setSearchQuery(stringValue);
	}, []);

	const addSearchTerm = useCallback(
		(term, additionalProps = {}, termType = null) => {
			console.log("addSearchTerm called with:", {
				term,
				additionalProps,
				termType,
			});

			// Handle different input types
			let cleanTerm;
			let termData;

			if (typeof term === "string") {
				cleanTerm = term.trim();
				if (!cleanTerm) return;

				// Determine term type - prioritize explicit parameter over props
				const finalTermType = termType || additionalProps.type || "include";

				termData = {
					query: cleanTerm,
					text: cleanTerm,
					threshold:
						additionalProps.threshold ??
						(finalTermType === "exclude"
							? AUDIENCE_EXCLUDE_THRESHOLD
							: AUDIENCE_INCLUDE_THRESHOLD),
					originalScore: additionalProps.originalScore,
					type: finalTermType,
					score:
						additionalProps.score ||
						(finalTermType === "exclude"
							? AUDIENCE_EXCLUDE_RELEVANCE
							: AUDIENCE_INCLUDE_RELEVANCE),
				};
			} else if (
				term &&
				typeof term === "object" &&
				(term.query || term.text)
			) {
				// Handle object input (already formatted)
				termData = {
					...term,
					query: term.query || term.text,
					text: term.text || term.query,
				};
				cleanTerm = term.query || term.text;
			} else {
				console.warn("Invalid term type:", typeof term, term);
				return;
			}

			console.log("Adding term with type:", termData.type, "Term:", termData);

			// Add to appropriate array based on term type
			if (termData.type === "exclude") {
				console.log("Adding to excludeTerms array");
				setExcludeTerms((prev) => {
					if (prev.some((t) => (t.query || t.text) === cleanTerm)) {
						console.log("Exclude term already exists:", cleanTerm);
						return prev;
					}
					return [...prev, termData];
				});
			} else {
				console.log("Adding to searchTerms array");
				setSearchTerms((prev) => {
					if (prev.some((t) => (t.query || t.text) === cleanTerm)) {
						console.log("Include term already exists:", cleanTerm);
						return prev;
					}
					return [...prev, termData];
				});
			}

			setSearchQuery("");
		},
		[],
	);

	// Add separate function for exclude terms for cleaner API
	const addExcludeTerm = useCallback(
		(term, additionalProps = {}) => {
			console.log("addExcludeTerm called with:", { term, additionalProps });
			return addSearchTerm(term, additionalProps, "exclude");
		},
		[addSearchTerm],
	);

	const removeSearchTerm = useCallback((termToRemove) => {
		console.log(
			"removeSearchTerm called with:",
			termToRemove,
			typeof termToRemove,
		);

		setSearchTerms((prev) => {
			// Handle both string (query) and object (full term) removal
			const filtered = prev.filter((t) => {
				if (typeof termToRemove === "string") {
					return (t.query || t.text) !== termToRemove;
				} else if (termToRemove && typeof termToRemove === "object") {
					return (
						(t.query || t.text) !== (termToRemove.query || termToRemove.text)
					);
				}
				return true;
			});

			console.log("Terms after removal:", filtered);
			return filtered;
		});
	}, []);

	const clearSearchTerms = useCallback(() => {
		setSearchQuery("");
		setSearchTerms([]);
		setExcludeTerms([]);
	}, []);

	const resetSearch = useCallback(() => {
		setSearchQuery("");
		setSearchTerms([]);
		setExcludeTerms([]);
		setIsSearching(false);
		setSearchResults({
			success: false,
			audiences: [],
			totalRecords: 0,
			searchSummary: null,
			hasMore: false,
			currentPage: 1,
			totalPages: 1,
		});
		setError(null);
		setHasSearched(false);
		setSearchOptions({
			sortBy: "compositeScore",
			sortOrder: "DESC",
			pageSize: 100,
			currentPage: 0,
		});
	}, []);

	// File upload handlers
	const handleFileUpload = useCallback(
		async (file, listType = "includeList") => {
			if (!file) return;

			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					...prev[listType],
					isUploading: true,
					error: null,
				},
			}));

			try {
				const fileContent = await file.text();
				const audienceIds = parseAudienceCSV(fileContent);

				setUploadStates((prev) => ({
					...prev,
					[listType]: {
						isUploading: false,
						fileName: file.name,
						audienceIds,
						error: null,
						isUploaded: true,
					},
				}));

				// Update filters with uploaded IDs
				if (listType === "includeList") {
					setFilters((prev) => ({
						...prev,
						uploadedAudienceIds: {
							...prev.uploadedAudienceIds,
							includeList: audienceIds,
						},
					}));
				} else if (listType === "blockedList") {
					setFilters((prev) => ({
						...prev,
						uploadedAudienceIds: {
							...prev.uploadedAudienceIds,
							excludeList: audienceIds,
						},
					}));
				}

				// Call external handler if provided
				if (listType === "includeList" && onUploadAudienceList) {
					onUploadAudienceList(audienceIds, file.name);
				} else if (listType === "blockedList" && onUploadBlockedAudienceList) {
					onUploadBlockedAudienceList(audienceIds, file.name);
				}
			} catch (error) {
				console.error("File upload error:", error);
				setUploadStates((prev) => ({
					...prev,
					[listType]: {
						...prev[listType],
						isUploading: false,
						error: error.message || "Failed to upload file",
					},
				}));
			}
		},
		[onUploadAudienceList, onUploadBlockedAudienceList],
	);

	// Clear uploaded list
	const clearUploadedList = useCallback((listType = "includeList") => {
		setUploadStates((prev) => ({
			...prev,
			[listType]: {
				isUploading: false,
				fileName: null,
				audienceIds: [],
				error: null,
				isUploaded: false,
			},
		}));

		// Update filters
		if (listType === "includeList") {
			setFilters((prev) => ({
				...prev,
				uploadedAudienceIds: {
					...prev.uploadedAudienceIds,
					includeList: [],
				},
			}));
		} else if (listType === "blockedList") {
			setFilters((prev) => ({
				...prev,
				uploadedAudienceIds: {
					...prev.uploadedAudienceIds,
					excludeList: [],
				},
			}));
		}
	}, []);

	// Computed values
	const getActiveFilterCount = useMemo(() => {
		let count = 0;
		const arrayFilters = [
			"accountName",
			"directAudienceProvider",
			"status",
			"type",
			"pricing",
			"dataSource",
			"region",
			"entity",
			"event",
			"primaryPersona",
			"audienceStrategy",
			"createdWith",
			"shareable_openx_audience",
		];

		arrayFilters.forEach((key) => {
			if (filters[key]?.length > 0) count++;
		});

		if (filters.openAudienceId?.trim()) count++;
		if (filters.publisherAccountId?.trim()) count++;
		if ((filters.reach || 0) > 0) count++;
		if ((filters.estimated_reach || 0) > 0) count++;
		if ((filters.CPRMarketRequests || 0) > 0) count++;
		if ((filters.Impressions || 0) > 0) count++;

		return count;
	}, [filters]);

	const hasActiveFilters = getActiveFilterCount > 0;

	const computedSearchValues = useMemo(
		() => ({
			hasResults: searchResults?.audiences?.length > 0,
			hasError: !!error,
			isLoadingMore: isSearching && searchResults?.audiences?.length > 0,
			hasSearchTerms: searchTerms.length > 0 || excludeTerms.length > 0,
			showEmptyState:
				hasSearched &&
				!isSearching &&
				!searchResults?.audiences?.length &&
				!error,
		}),
		[searchResults, error, isSearching, searchTerms, excludeTerms, hasSearched],
	);

	// Effect for restoring scroll position
	useEffect(() => {
		restoreScrollPosition();
	}, [filters, restoreScrollPosition]);

	console.log("useAudienceSearchWithFilters hook initialized with LLM support");

	return {
		// Sort options
		sortOptions,
		sortDirectionOptions,

		// Filter state and functions
		filters,
		defaultFilters,
		filterValues,
		filterValuesLoading,
		filterValuesError,
		filterValuesLastFetch,
		loadFilterValues,
		expandedSections,
		expandedCategories,
		searchInputs,
		debouncedSearchInputs,
		uploadStates,
		showUploadTooltip,
		setShowUploadTooltip,
		availableOptions,

		// Filter functions
		updateFilter,
		toggleArrayFilter,
		clearAllFilters,
		toggleSection,
		toggleCategory,
		activeFilterCount: getActiveFilterCount,
		hasActiveFilters,
		filtersContentRef,
		saveScrollPosition,
		restoreScrollPosition,

		// Search state and functions
		searchQuery,
		searchTerms,
		excludeTerms,
		searchOptions,
		isSearching,
		searchResults,
		error,
		hasSearched,

		// Search input handlers
		onSearchInputChange,
		handleSearchInputChange,
		addSearchTerm,
		addExcludeTerm,
		removeSearchTerm,
		clearSearchTerms,

		// Main search functions
		triggerSearch, // MOVED UP - now available for use
		onSearch: handleAudienceSearch,
		executeSearch: handleAudienceSearch,
		performSearch: handleAudienceSearch,

		// LLM support functions
		injectSearchResults,
		triggerSearchWithOverride,

		// Upload functions
		handleFileUpload,
		clearUploadedList,

		// Utility functions
		resetSearch,
		buildCompleteSearchPayload,

		// Computed values
		...computedSearchValues,

		// Debug function
		getDebugInfo: () => ({
			currentFilters: filters,
			samplePayload: buildCompleteSearchPayload({}, filters),
			hookType: "fixed-with-llm-support",
			sortOptions,
			currentSorting: {
				sortBy: filters.sortBy,
				sortDirection: filters.sortDirection,
			},
		}),
	};
};
